import React, { Fragment } from "react";
import Box from "@material-ui/core/Box";
import { getDateString } from "utility";
import { Settlement } from "./AccountSettlementHistory";
import { TicketIcon } from "features/icon/TicketIcon";
import ReceiptIcon from "@material-ui/icons/Receipt";
import ReportProblemIcon from "@material-ui/icons/ReportProblem";
import type { PayType } from "@spwn/types";
import type { ActiveTransaction } from "@spwn/types/firebase/firestore";
import { useI18n } from "hooks/i18n/i18n";
import { isPayPalPendingError } from "utility/paypal";
import { TimezoneDisplay } from "i18n/timezone";
import { withNoTranslate } from "i18n/noTranslate";
import { LegacyPortalShippingStatusDispatchStatus } from "@spwn-portal/grpc";

export const AccountSettlementHistoryItem: React.FC<{
  history: Settlement;
  setSelectedSettlementHistory: (orderId: number, history: Settlement) => void;
  createConfirmCvsPaymentData: (transactionData: ActiveTransaction) => void;
  dispatchStatus?: LegacyPortalShippingStatusDispatchStatus;
  trackingUrl?: string;
}> = ({
  history,
  setSelectedSettlementHistory,
  createConfirmCvsPaymentData,
  dispatchStatus: _dispatchStatus,
  trackingUrl: _trackingUrl,
}) => {
  const isPurchased =
    !isNaN(Number(history.orderId)) && history.status === "PAYSUCCESS";

  const totalPrice = history?.totalPrice
    ? history.totalPrice
    : history.products.reduce(
        (acc, cur) => acc + cur.price_jpy * cur.count,
        0
      ) + (history.shippingFee ?? 0);

  return (
    <Box
      padding={["16px 8px", "16px 16px"]}
      borderTop={["1px solid #e6e5e6", "none"]}
      borderBottom={"1px solid #e6e5e6"}
    >
      {/* {history.createdAt && (
        <Box marginBottom={"12px"}>
          {getDateString(history.createdAt.toDate())}
        </Box>
      )} */}

      <Box
        display={"grid"}
        gridTemplateColumns={["1fr", "3fr 9fr 3fr 2fr 2fr 1fr"]}
        gridGap={["8px", "24px"]}
        alignItems={"center"}
      >
        {history.createdAt && (
          <Box component={"p"} marginBottom={["8px", "0"]} fontSize={"14px"}>
            {getDateString(history.createdAt.toDate())} <TimezoneDisplay />
          </Box>
        )}

        <Box fontSize={"14px"}>
          <Box
            display={"grid"}
            gridTemplateColumns={"1fr"}
            gridGap={"16px"}
            alignItems={"center"}
          >
            {history.products.map((product) => {
              return (
                <Box
                  key={product._id}
                  display={"grid"}
                  gridTemplateColumns={"56px 1fr max-content"}
                  gridGap={"16px"}
                  alignItems={"center"}
                >
                  {product.thumbnail ? (
                    <Box
                      border={"1px solid #e6e5e6"}
                      width={"56px"}
                      height={"56px"}
                      overflow={"hidden"}
                    >
                      <img
                        src={product.thumbnail}
                        alt={product.eventLongTitle}
                        width={"56px"}
                        height={"auto"}
                      />
                    </Box>
                  ) : (
                    <Box
                      display={"flex"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      width={"56px"}
                      height={"56px"}
                      border={"1px solid #e6e5e6"}
                    >
                      <TicketIcon width="40px" height="40px" />
                    </Box>
                  )}

                  <Box fontSize={"12px"}>
                    <Box fontSize={"14px"} fontWeight={"bold"}>
                      <span {...withNoTranslate()}>
                        {product.eventLongTitle}
                      </span>
                    </Box>
                    <Box>{product.name}</Box>
                    {product.subClassName && <Box>{product.subClassName}</Box>}
                  </Box>

                  <Box>× {product.count}</Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box component={"p"} fontSize={"14px"} textAlign={["left", "center"]}>
          {getPaymentType(history.payType)}
        </Box>

        <Box textAlign={["left", "center"]}>
          {isPurchased ? (
            <Box
              component={"p"}
              color={"#00c2ae"}
              fontSize={"14px"}
              fontWeight={"bold"}
            >
              決済完了
            </Box>
          ) : (
            <Box
              component={"p"}
              color={"#975a16"}
              fontSize={"14px"}
              fontWeight={"bold"}
            >
              決済待ち
            </Box>
          )}
        </Box>

        <Box
          component={"p"}
          fontSize={["16px", "16px"]}
          fontWeight={"bold"}
          textAlign={"right"}
        >
          <span {...withNoTranslate()}>¥{totalPrice.toLocaleString()}</span>
        </Box>

        <Box textAlign={["right", "center"]}>
          {isPurchased && (
            <Box
              onClick={() =>
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                setSelectedSettlementHistory(history.orderId!, history)
              }
              aria-label="領収書を表示"
            >
              <Box
                display={["grid", "inline-block"]}
                gridTemplateColumns={"max-content max-content"}
                gridGap={"4px"}
                alignItems={"center"}
                justifyContent={"right"}
                style={{ cursor: "pointer" }}
              >
                <ReceiptIcon style={{ color: "#505050" }} />
                <Box
                  component={"p"}
                  display={["block", "none"]}
                  fontSize={"14px"}
                  style={{
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                >
                  領収書を表示
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {!isPurchased && (
        <Box marginTop={"16px"}>
          <AlertPaymentProcedureRequest
            settlementHistory={history}
            onClickAction={() => createConfirmCvsPaymentData(history)}
          />
        </Box>
      )}
    </Box>
  );
};

const getPaymentType = (paymentType: PayType | null) => {
  switch (paymentType) {
    case "Card":
    case "Member":
      return "カード決済";
    case "CVS":
      return "コンビニ決済";
    case "Phone":
      return "キャリア決済";
    case "Free":
      return "無料";
    case "Venue":
      return "会場決済";
    case "Stripe":
      return "Stripe決済";
    case "PayPal":
      return "PayPal決済";
    default:
      return "不明";
  }
};

const AlertPaymentProcedureRequest: React.FC<{
  settlementHistory: Settlement;
  onClickAction: () => void;
}> = ({ onClickAction, settlementHistory }) => {
  const { t } = useI18n();

  return (
    <Box
      display={"grid"}
      gridTemplateColumns={"max-content auto max-content"}
      gridGap={"8px"}
      alignItems={"center"}
      padding={"8px 8px"}
      bgcolor={"#fffaf0"}
      border={"1px solid #d69e2e"}
      borderRadius={"4px"}
    >
      <Box minHeight={"24px"}>
        <ReportProblemIcon htmlColor={"#975a16"} />
      </Box>

      {settlementHistory.payType === "PayPal" &&
      settlementHistory.status === "UNPROCESS" &&
      isPayPalPendingError(settlementHistory.errorInfo) ? (
        <Box
          display={"block"}
          color={"#975a16"}
          fontSize={"14px"}
          lineHeight={"1.4em"}
        >
          <Box fontWeight={"bold"}>
            <p>{t("confirmCvsPayment.paypalPendingReview")}</p>
          </Box>
        </Box>
      ) : (
        <Fragment>
          <Box
            display={"grid"}
            gridTemplateColumns={["1fr", "max-content max-content"]}
            gridGap={["0", "8px"]}
            alignItems={"center"}
            color={"#975a16"}
            fontSize={"14px"}
          >
            <Box fontWeight={"bold"}>
              {t("settlementHistory.paymentProcedureRequest")}
            </Box>
            <Box lineHeight={"1.2em"}>
              {t("settlementHistory.necessaryProceduresRemain")}
            </Box>
          </Box>

          <Box onClick={onClickAction} aria-label="決済を続ける">
            <Box
              display={"inline-block"}
              minWidth={"calc(6em + 20px)"}
              padding={"6px 10px"}
              color={"#fff"}
              fontSize={"14px"}
              bgcolor={"#975a16"}
              borderRadius={"4px"}
              style={{
                cursor: "pointer",
              }}
            >
              {t("settlementHistory.continueToSettle")}
            </Box>
          </Box>
        </Fragment>
      )}
    </Box>
  );
};
